// src/pages/booking/ResourcesCatalogPage.tsx
// Resources catalog — the booking module's home. Browse, search, filter,
// and (for providers) create bookable resources.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Input, Alert } from '@react/components';
import { Select } from '@/components/Select';
import type { SelectOption } from '@/components/Select';
import { useScope } from '@/hooks/useScope';
import { ResourceCard } from '@/features/booking/ResourceCard';
import { ResourceFormDialog } from '@/features/booking/ResourceFormDialog';
import { errMessage } from '@/features/booking/lib';
import { ListResources } from '@core/api/endpoints/booking/list_resources';
import { SearchAvailableResources } from '@core/api/endpoints/booking/search_available_resources';
import { ListBookingTypes } from '@core/api/endpoints/booking/list_booking_types';
import type { BookableResource, BookingTypeDefinition } from '@core/types/booking';

export const ResourcesCatalogPage = observer(() => {
  const { t } = useI18n();
  const scope = useScope();

  const [resources, setResources] = useState<BookableResource[]>([]);
  const [bookingTypes, setBookingTypes] = useState<BookingTypeDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    ListBookingTypes({ product_id: scope.productId, limit: 100 })
      .then((res) => setBookingTypes(res.definitions ?? []))
      .catch(() => {});
  }, [scope.productId]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (availableOnly) {
        const now = new Date();
        const in7 = new Date(now.getTime() + 7 * 86400000);
        const res = await SearchAvailableResources({
          scope: { type_id: '', type_name: 'platform', id: scope.platformId, platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId },
          booking_type_id: typeFilter || undefined,
          start_time: now.toISOString(),
          end_time: in7.toISOString(),
          search_query: search || undefined,
        });
        setResources((res.results ?? []).map((r) => r.resource).filter((r): r is BookableResource => !!r));
      } else {
        const res = await ListResources({
          booking_type_id: typeFilter || undefined,
          q: search || undefined,
          limit: 100,
        });
        setResources(res.resources ?? []);
      }
    } catch (e) {
      setError(errMessage(e, t('booking.resource.loadError')));
    } finally {
      setIsLoading(false);
    }
  }, [scope.productId, scope.platformId, scope.tenantId, typeFilter, availableOnly, search, t]);

  useEffect(() => {
    load();
  }, [load]);

  const typeOptions: SelectOption[] = [
    { value: '', label: t('booking.resource.allTypes') },
    ...bookingTypes.map((bt) => ({ value: bt.entity?.id ?? '', label: bt.display_name || bt.slug })),
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('booking.catalog.title')}</h1>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/booking/my-bookings" className="text-sm text-primary hover:underline">
            {t('booking.catalog.myBookings')}
          </Link>
          <Link to="/booking/admin" className="text-sm text-primary hover:underline">
            {t('booking.catalog.admin')}
          </Link>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            {t('booking.resource.createTitle')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('booking.catalog.searchPlaceholder')}
          className="max-w-xs"
          fullWidth={false}
        />
        <div className="w-48">
          <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} options={typeOptions} />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-foreground">
          <input type="checkbox" checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} />
          {t('booking.catalog.availableThisWeek')}
        </label>
      </div>

      {error && (
        <Alert variant="error">
          {error}
          <Button size="sm" variant="secondary" className="ml-2" onClick={load}>
            {t('booking.retry')}
          </Button>
        </Alert>
      )}

      {isLoading && resources.length === 0 && !error && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-56 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && !error && resources.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('booking.catalog.empty')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('booking.catalog.subtitle')}</p>
          <Button size="sm" className="mt-3" onClick={() => setCreateOpen(true)}>
            {t('booking.resource.createTitle')}
          </Button>
        </div>
      )}

      {resources.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {resources.map((r) => (
            <ResourceCard key={r.entity?.id} resource={r} />
          ))}
        </div>
      )}

      <ResourceFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        resource={null}
        bookingTypes={bookingTypes}
        scope={scope}
        onSaved={load}
      />
    </div>
  );
});
